import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiUsers, FiUser } from 'react-icons/fi';
import { apiGet } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Skeleton } from '@/shared/components/ui/Skeleton';

interface ConnectedUser {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar: string | null;
  companyName: string;
  companyId: string;
  connectedAt: string;
}

interface ConnectedUsersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

export const ConnectedUsersDrawer: React.FC<ConnectedUsersDrawerProps> = ({ isOpen, onClose }) => {
  const { data: users, isLoading } = useQuery({
    queryKey: ['admin-connected-users'],
    queryFn: () => apiGet<ConnectedUser[]>('/dashboard/connected-users'),
    enabled: isOpen,
    refetchInterval: isOpen ? 10000 : false,
  });

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      icon={<FiUsers className="w-5 h-5" />}
      title="Usuarios Conectados"
      subtitle={users ? `${users.length} usuario${users.length !== 1 ? 's' : ''} online` : undefined}
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3">
              <Skeleton width={40} height={40} variant="circular" />
              <div className="flex-1 space-y-2">
                <Skeleton width={160} height={16} />
                <Skeleton width={120} height={12} />
              </div>
            </div>
          ))}
        </div>
      ) : users && users.length > 0 ? (
        <div className="space-y-1">
          {users.map((user) => (
            <div
              key={user.userId}
              className="flex items-center gap-3 p-3 rounded-lg transition-colors"
              style={{ border: '1px solid var(--theme-card-border)' }}
            >
              {/* Avatar */}
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.firstName}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#f3e8ff', color: '#8b5cf6' }}
                >
                  <FiUser className="w-5 h-5" />
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                  {user.email}
                </p>
              </div>

              {/* Company + time */}
              <div className="text-right flex-shrink-0">
                <p className="text-xs font-medium" style={{ color: 'var(--theme-primary)' }}>
                  {user.companyName}
                </p>
                <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                  desde {formatTime(user.connectedAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <FiUsers className="w-12 h-12 mb-3" style={{ color: 'var(--theme-muted-text)', opacity: 0.5 }} />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Nenhum usuario conectado
          </p>
        </div>
      )}
    </Drawer>
  );
};
